import { apiClient } from "../services/apiService";
import {
  BREAKIN_STATUS_LABELS,
  toBreakinCases,
  toBreakinReport,
  toReviewers,
} from "./breakinData";
import { toLegacyAssessment } from "../services/adapters/hegicAssessment";
import { toUploads } from "../services/adapters/hegicUploads";

/**
 * Break-in is a second Hegic deployment, not a second login.
 *
 * Its host and source_name both differ from claims — hegicb.api.camcom.ai
 * with `hdfcergo`, against hegicpp.api with `hdfcergo_pp` — so every call here
 * states both, using the same per-request `apiHost` that analytics uses. The
 * session stays the Hegic claims session throughout.
 *
 * NO SSO EXCHANGE. The portal calls POST /sso_login (email + access_key) to
 * mint a break-in token, and we do not need to: the two deployments validate
 * the same JWT. Verified by taking a token issued by the break-in host and
 * calling the CLAIMS host with it — 200, not 401. So the Bearer the app
 * already holds is accepted here, and an access key we would have to obtain,
 * store and rotate never enters the picture.
 *
 * If that ever stops being true the symptom is unambiguous — every call below
 * 401s at once — and /sso_login is the documented way back.
 */

// Dev goes through the proxy: hegicb allows its own portal origin, not
// localhost. Production talks to the host directly.
export const BREAKIN_API =
  process.env.NODE_ENV === "development"
    ? "/hegic-breakin-api"
    : "https://hegicb.api.camcom.ai";

export const BREAKIN_SOURCE = "hdfcergo";

const get = (path, params = {}) =>
  apiClient.get(path, {
    apiHost: BREAKIN_API,
    params: { source_name: BREAKIN_SOURCE, ...params },
  });

/** Headline counts. Accepts an optional window; without one it is all time. */
export const getBreakinReport = async ({ startDate, endDate } = {}) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await get("/assessment_report", params);
  return toBreakinReport(response.data);
};

/**
 * One page of claims.
 *
 * Offset paging, not page numbers: the response's `next.offset` is what the
 * following page asks for.
 */
export const getBreakinCases = async ({ limit = 15, offset = 0 } = {}) => {
  const params = { per_page_limit: limit };
  if (offset) params.offset = offset;
  const response = await get("/assessment_list/all", params);
  return toBreakinCases(response.data);
};

/** Reviewer throughput for a window. Both dates are required by the route. */
export const getBreakinReviewers = async ({ startDate, endDate } = {}) => {
  const response = await get("/get_allreview_data", {
    start_date: startDate,
    end_date: endDate,
  });
  return toReviewers(response.data);
};

/**
 * Its own media host. Probed: hegicb.media serves /root/media/hdfcergo/…
 * (200 image/jpeg) and hegicpp.media 404s on the same path, so the claims
 * media host is not a fallback.
 */
export const BREAKIN_MEDIA = "https://hegicb.media.camcom.ai";

/**
 * One break-in case, from the three routes that describe it.
 *
 * The payloads are the SAME CONTRACT as Hegic claims — /assessment answers
 * claim_summary, assess, parts, partdamages, damages, carImages and
 * assess_history, and /vehicle the same record — so this reuses the claims
 * adapters wholesale rather than growing a second copy of the damage-scale
 * and history-merge logic, which is the part most expensive to get right.
 *
 * Fetched together because the adapter needs the vehicle to fill the header:
 * /assessment carries no make, model or plate.
 */
export const getBreakinCase = async (refNum) => {
  if (!refNum) throw new Error("getBreakinCase: ref_num is required");

  const [vehicle, assessment, uploads] = await Promise.all([
    get("/vehicle", { ref_num: refNum }).then((r) => r.data),
    get("/assessment", { ref_num: refNum }).then((r) => r.data),
    // The originals. A failure here costs the photographs, not the page.
    get("/uploads", { ref_num: refNum })
      .then((r) => toUploads(r.data))
      .catch(() => []),
  ]);

  const detail = toLegacyAssessment(assessment, { vehicle });
  return detail && { ...detail, userUploads: uploads };
};

/**
 * Break-in, as a deployment the claims detail page can be pointed at.
 *
 * Frozen and defined once at module level on purpose: it goes into effect
 * dependency arrays, and an object literal built in the route element would
 * be a new identity on every render, re-fetching the case forever.
 *
 * `features` is empty because this backend has neither — /poi_tracking is 404
 * here and the digital passport is served by the claims portal — so the page
 * shows neither tab instead of two that fail.
 */
export const BREAKIN_DEPLOYMENT = Object.freeze({
  key: "breakin",
  apiHost: BREAKIN_API,
  sourceName: BREAKIN_SOURCE,
  mediaBaseUrl: BREAKIN_MEDIA,
  // hegicb.media serves /root/media/hdfcergo/… verbatim; no re-rooting.
  mediaPathPrefix: "",
  features: Object.freeze({ poi: false, passport: false }),
  listPath: "/redesign/breakin",
  listLabel: "Break-in",
  // Same codes, different meanings: 3 is Completed here where claims reads it
  // as "AI Assessed". Without this the detail page contradicts the list it
  // was opened from, on the same case.
  statusLabels: BREAKIN_STATUS_LABELS,
});
